(function (root) {
  'use strict';
  function createCache({ttl = 5000, limit = 200, now = Date.now} = {}) {
    const cache = new Map(), pending = new Map();
    let generation = 0;
    function invalidate() { generation++; cache.clear(); pending.clear(); }
    function read(key, loader, fresh = false) {
      const entry = cache.get(key);
      if (!fresh && entry && entry.expires > now()) return Promise.resolve(structuredClone(entry.value));
      if (!fresh && pending.has(key)) return pending.get(key).then(structuredClone);
      const version = generation;
      const promise = Promise.resolve().then(loader).then(value => {
        if (version === generation) {
          cache.delete(key);
          cache.set(key, {value:structuredClone(value), expires:now() + ttl});
          while (cache.size > limit) cache.delete(cache.keys().next().value);
        }
        return value;
      }).finally(() => { if (pending.get(key) === promise) pending.delete(key); });
      if (!fresh) pending.set(key, promise);
      return promise.then(structuredClone);
    }
    return {read, invalidate};
  }
  function createProductBatcher(fetchChunk, {now = Date.now, ttl = 30000, limit = 1000} = {}) {
    const cache = new Map(), waiting = new Map(), inflight = new Map();
    let scheduled = false, generation = 0;
    const identify = (id, context) => JSON.stringify([context, String(id)]);
    function invalidate() { generation++; cache.clear(); inflight.clear(); }
    async function flush() {
      scheduled = false;
      const groups = [...waiting.values()]; waiting.clear();
      for (const group of groups) {
        const rows = [...group.rows.values()];
        for (let i = 0; i < rows.length; i += 250) {
          const chunk = rows.slice(i, i + 250);
          try {
            const products = await fetchChunk(chunk.map(row => row.id), group.context);
            if (!Array.isArray(products)) throw Error('Invalid products response');
            const byId = new Map(products.map(product => [String(product.id).split('/').pop(), product]));
            for (const row of chunk) {
              const value = byId.get(row.id) || null;
              if (row.generation === generation) {
                cache.delete(row.key); cache.set(row.key, {value, expires:now() + ttl});
                while (cache.size > limit) cache.delete(cache.keys().next().value);
              }
              row.resolve(value);
            }
          } catch (error) { chunk.forEach(row => row.reject(error)); }
          finally { chunk.forEach(row => { if (inflight.get(row.key) === row.promise) inflight.delete(row.key); }); }
        }
      }
    }
    function one(id, context) {
      const key = identify(id, context), entry = cache.get(key);
      if (entry && entry.expires > now()) return Promise.resolve(entry.value);
      if (inflight.has(key)) return inflight.get(key);
      let resolve, reject;
      const promise = new Promise((yes,no) => {resolve=yes;reject=no;});
      const row = {id,key,resolve,reject,promise,generation};
      const groupKey = JSON.stringify([generation, context]);
      if (!waiting.has(groupKey)) waiting.set(groupKey,{context,rows:new Map()});
      waiting.get(groupKey).rows.set(key,row); inflight.set(key,promise);
      if (!scheduled) { scheduled=true; queueMicrotask(flush); }
      return promise;
    }
    return {invalidate, async fetch(ids, context) {
      const values = await Promise.all([...new Set(ids.map(String))].map(id => one(id,context)));
      return structuredClone(values.filter(Boolean));
    }};
  }
  function install(sdk, config = {}) {
    if (!sdk || sdk.__dacSharedData) return;
    const originals = {}, changes = ['addToList','deleteFromList','updateList','updateListItem','renameList','createList','createLists','deleteList'];
    const invalidate = () => {sdk.__dacService?.invalidate(); root.DACWishlistProducts?.invalidate?.();};
    for (const name of changes) {
      if (typeof sdk[name] !== 'function') continue;
      originals[name]=sdk[name];
      sdk[name]=function(...args) {
        invalidate();
        const first = args.findIndex(value => typeof value === 'function');
        if (first >= 0) for (let i=first;i<args.length;i++) if (typeof args[i] === 'function') {
          const callback=args[i]; args[i]=function(...values) {invalidate();return callback.apply(this,values);};
        }
        return originals[name].apply(this,args);
      };
    }
    root.addEventListener('pageshow',invalidate);
    root.addEventListener('focus',invalidate);
    root.addEventListener('storage',invalidate);
    root.document.addEventListener('visibilitychange',()=>{if(root.document.visibilityState==='visible')invalidate();});
    root.document.addEventListener('dac:list-change',invalidate);
    for (const name of ['addedToWishlist','removedFromWishlist','customerInfoRefreshed','regidRefreshed']) {
      const event = sdk.JSEvents?.[name]; if(event)sdk.evtLayer?.addEventListener(event,invalidate);
    }
    sdk.__dacSharedData = {invalidate,originals};
    return sdk.__dacSharedData;
  }
  const api={createCache,createProductBatcher,install};
  if (typeof module !== 'undefined' && module.exports) module.exports=api;
  if (root && root.document) {
    root.DACWishlistData=api;
    (root.SwymCallbacks=root.SwymCallbacks||[]).unshift(sdk=>install(sdk));
    if(root._swat?.fetchLists)install(root._swat);
  }
})(typeof window === 'undefined' ? null : window);
